import { useCallback, useMemo } from "react";
import { useApolloClient, useMutation } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";

import {
  BulkAddIssueAssigneesDocument,
  BulkArchiveIssuesDocument,
  BulkMoveIssuesToStageDocument,
  BulkRemoveIssueAssigneesDocument,
  BulkSetPrimaryAssigneeDocument,
  BulkUnarchiveIssuesDocument,
  BulkUpdateIssuesDocument,
  DeleteIssueDocument,
  type EntityType,
  IssueListDataDocument,
  IssueListGroupCountsDocument,
  IssuesDocument,
  MoveIssueToStageDocument,
  MoveIssueToWorkflowDocument,
  UpdateIssueAssigneesDocument,
  requireBulkSuccess,
} from "#app/lib/data";
import { resultMessage, toastMutationError } from "#app/lib/utils/toast-errors";
import { AnalyticsContext, AnalyticsView, KanbanEvent, useAnalytics } from "#app/lib/analytics";
import { removeFromConnection } from "#app/lib/data/connection-cache-utils";
import { useTrashMutations } from "#app/hooks/settings/use-trash-mutations";
import { useIssueViewOptional } from "#app/components/issue-view/use-issue-view";
import { useRecentItems } from "#app/lib/hooks/use-recent-items";

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/** Which issues a bulk write reaches: loaded rows by id, or a select-all's
 *  server-side filter (which also reaches rows the client never loaded). */
export type IssueSelector = { issueIds: string[] } | { where: Record<string, unknown> };

export function useIssueListMutations() {
  const { track } = useAnalytics();
  const [changeStageMutation] = useMutation(MoveIssueToStageDocument);
  const [moveToWorkflowMutation] = useMutation(MoveIssueToWorkflowDocument);
  const [updateAssigneesMutation] = useMutation(UpdateIssueAssigneesDocument);
  const [deleteMutation] = useMutation(DeleteIssueDocument);
  const apolloClient = useApolloClient();
  const { restoreIssues } = useTrashMutations();
  const issueView = useIssueViewOptional();
  const { forgetVisit } = useRecentItems();
  const [bulkMoveMutation] = useMutation(BulkMoveIssuesToStageDocument);
  const issueRefetchQueries = { refetchQueries: [IssueListDataDocument, IssuesDocument, IssueListGroupCountsDocument] };
  const [bulkAssigneesMutation] = useMutation(BulkAddIssueAssigneesDocument, issueRefetchQueries);
  const [bulkRemoveAssigneesMutation] = useMutation(BulkRemoveIssueAssigneesDocument, issueRefetchQueries);
  const [setPrimaryAssigneeMutation] = useMutation(BulkSetPrimaryAssigneeDocument, issueRefetchQueries);
  // The bulk writers behind Teams, Source, Custom field, Move to workflow,
  // Archive and Unarchive: one request per batch, by ids or by a select-all
  // `where`. The lists refetch once, when it lands.
  const [bulkUpdateMutation] = useMutation(BulkUpdateIssuesDocument, issueRefetchQueries);
  const [bulkArchiveMutation] = useMutation(BulkArchiveIssuesDocument, issueRefetchQueries);
  const [bulkUnarchiveMutation] = useMutation(BulkUnarchiveIssuesDocument, issueRefetchQueries);

  const changeStage = useCallback(
    async (issueId: string, stageId: string) => {
      try {
        await changeStageMutation({
          variables: {
            issueId,
            stageId,
            sortOrder: Number.MAX_SAFE_INTEGER,
          },
        });
      } catch (err) {
        toastMutationError(err, "Failed to update stage");
      }
    },
    [changeStageMutation],
  );

  // The list's Workflow cell: one `kanban.move_workflow` per move that
  // lands, `via: "table_cell"` on the list screen.
  const moveToWorkflow = useCallback(
    async (issueId: string, workflowId: string, stageId?: string) => {
      try {
        // A move changes the row's workflow AND stage, so the lists refetch: a
        // single-workflow list drops the row, a cross-workflow one re-buckets it.
        const result = await moveToWorkflowMutation({
          variables: { issueId, workflowId, ...(stageId ? { stageId } : {}) },
          refetchQueries: [IssueListDataDocument, IssuesDocument, IssueListGroupCountsDocument],
        });
        const payload = result.data?.updateIssue;
        if (payload?.__typename !== "Issue") {
          throw new Error(resultMessage(payload) ?? "Move failed");
        }
        track({
          context: AnalyticsContext.Kanban,
          event: KanbanEvent.MoveWorkflow,
          data: { via: "table_cell", view: AnalyticsView.List, stagePicked: !!stageId },
        });
        // A single-workflow list drops the row, so the toast says where it went.
        toastManager.add({ title: "Moved to workflow", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to move to workflow");
      }
    },
    [moveToWorkflowMutation, track],
  );

  const updateAssignees = useCallback(
    async (issueId: string, entityIds: string[]) => {
      try {
        await updateAssigneesMutation({
          variables: {
            issueId,
            assignees: entityIds.map((id, index) => ({
              entityType: "user" as EntityType,
              entityId: id,
              isPrimary: index === 0,
            })),
          },
        });
      } catch (err) {
        toastMutationError(err, "Failed to update assignees");
      }
    },
    [updateAssigneesMutation],
  );

  // ---------------------------------------------------------------------------
  // Dedicated bulk mutations — single GraphQL call per operation.
  // ---------------------------------------------------------------------------

  const bulk = useMemo(
    () => ({
      changeStage: async (issueIds: string[], stageId: string) => {
        await bulkMoveMutation({ variables: { issueIds, stageId } });
      },
      changeStageByWhere: async (where: Record<string, unknown>, stageId: string) => {
        await bulkMoveMutation({ variables: { where, stageId } });
      },
      addAssignees: async (issueIds: string[], entityIds: string[]) => {
        await bulkAssigneesMutation({
          variables: {
            issueIds,
            assignees: entityIds.map((id) => ({
              entityType: "user" as EntityType,
              entityId: id,
            })),
          },
        });
      },
      addAssigneesByWhere: async (where: Record<string, unknown>, entityIds: string[]) => {
        await bulkAssigneesMutation({
          variables: {
            where,
            assignees: entityIds.map((id) => ({
              entityType: "user" as EntityType,
              entityId: id,
            })),
          },
        });
      },
      replaceAssignees: async (issueIds: string[], entityIds: string[]) => {
        await Promise.all(
          issueIds.map((issueId) =>
            updateAssigneesMutation({
              variables: {
                issueId,
                assignees: entityIds.map((id, index) => ({
                  entityType: "user" as EntityType,
                  entityId: id,
                  isPrimary: index === 0,
                })),
              },
            }),
          ),
        );
      },
      removeAssignees: async (issueIds: string[], entityIds: string[]) => {
        await bulkRemoveAssigneesMutation({ variables: { issueIds, entityIds } });
      },
      removeAssigneesByWhere: async (where: Record<string, unknown>, entityIds: string[]) => {
        await bulkRemoveAssigneesMutation({ variables: { where, entityIds } });
      },
      setPrimaryAssignee: async (issueIds: string[], entityId: string) => {
        await setPrimaryAssigneeMutation({ variables: { issueIds, entityId } });
      },
      setPrimaryAssigneeByWhere: async (where: Record<string, unknown>, entityId: string) => {
        await setPrimaryAssigneeMutation({ variables: { where, entityId } });
      },
      // Teams change by delta: the server applies each to the issue's
      // current teams, so a row keeps the teams the selection did not share
      // and two quick toggles cannot overwrite each other.
      changeTeams: async (selector: IssueSelector, add: string[], remove: string[]) => {
        const { data } = await bulkUpdateMutation({
          variables: { ...selector, input: { addTeamIds: add, removeTeamIds: remove } },
        });
        requireBulkSuccess(data?.bulkUpdateIssues);
      },
      moveToWorkflow: async (selector: IssueSelector, workflowId: string, stageId: string) => {
        const { data } = await bulkUpdateMutation({
          variables: { ...selector, input: { workflowId, stageId } },
        });
        requireBulkSuccess(data?.bulkUpdateIssues);
      },
      setSource: async (selector: IssueSelector, issueSourceId: string) => {
        const { data } = await bulkUpdateMutation({
          variables: { ...selector, input: { issueSourceId } },
        });
        requireBulkSuccess(data?.bulkUpdateIssues);
      },
      // `undefined` clears the field the way the drawer's own clear does;
      // anything else is a single-key merge into each issue's values. The
      // caller passes DTO-space values (already through `fieldDto`).
      setCustomFieldValue: async (selector: IssueSelector, customFieldId: string, value: unknown) => {
        const input =
          value === undefined
            ? { clearCustomFieldIds: [customFieldId] }
            : { values: { [customFieldId]: value } };
        const { data } = await bulkUpdateMutation({ variables: { ...selector, input } });
        requireBulkSuccess(data?.bulkUpdateIssues);
      },
      archiveIssues: async (selector: IssueSelector) => {
        const { data } = await bulkArchiveMutation({ variables: selector });
        requireBulkSuccess(data?.bulkArchiveIssues);
      },
      unarchiveIssues: async (selector: IssueSelector) => {
        const { data } = await bulkUnarchiveMutation({ variables: selector });
        requireBulkSuccess(data?.bulkUnarchiveIssues);
      },
      // The API deletes one issue per call, so this settles a call per id and
      // evicts each from the list connection as it lands; the bar announces
      // one toast for the batch. There is no where-based variant, so a
      // select-all selection has to hide the action instead. Resolves to the
      // ids that reached the trash, so the Undo restores only those.
      deleteIssues: async (issueIds: string[]) => {
        const trashed: string[] = [];
        await Promise.allSettled(
          issueIds.map(async (issueId) => {
            const { data } = await deleteMutation({
              variables: { issueId },
              update(cache, { data }) {
                const result = data?.deleteIssue;
                if (result?.__typename === "Issue") {
                  removeFromConnection(
                    cache,
                    "issues",
                    { __typename: "Issue", issueId: result.issueId },
                    { evict: true },
                  );
                }
              },
            });
            if (data?.deleteIssue.__typename === "Issue") trashed.push(issueId);
          }),
        );
        // As the single delete does: a trashed Project is not left open
        // behind the toast, nor in Recent.
        for (const issueId of trashed) {
          issueView?.closeIssueIfOpen(issueId);
          forgetVisit({ kind: "issue", id: issueId });
        }
        const failed = issueIds.length - trashed.length;
        if (failed && !trashed.length) throw new Error(`${failed} of ${issueIds.length} failed`);
        if (failed) {
          toastManager.add({
            title: `${failed.toLocaleString()} Project${failed !== 1 ? "s" : ""} could not be deleted`,
            type: "error",
          });
        }
        return trashed;
      },
      // The bulk-delete Undo. The deletes evicted the rows from every list,
      // so the lists refetch once the batch is back.
      restoreIssues: async (issueIds: string[]) => {
        await restoreIssues(issueIds);
        await apolloClient.refetchQueries({
          include: [IssueListDataDocument, IssuesDocument, IssueListGroupCountsDocument],
        });
      },
    }),
    [
      apolloClient,
      bulkUpdateMutation,
      bulkArchiveMutation,
      bulkUnarchiveMutation,
      forgetVisit,
      issueView,
      restoreIssues,
      bulkMoveMutation,
      bulkAssigneesMutation,
      updateAssigneesMutation,
      bulkRemoveAssigneesMutation,
      setPrimaryAssigneeMutation,
      deleteMutation,
    ],
  );

  return { changeStage, moveToWorkflow, updateAssignees, bulk };
}
